import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCcw,
  Save,
  SkipForward,
  Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/ui/PaywallButton";

type WizardNavigationFooterProps = {
  currentMainStep: number;
  isLastSector: boolean;
  canGoBack: boolean;
  canGoNext: boolean;
  isSaving: boolean;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  matrixIsLocked?: boolean;
  matrixIsDefault?: boolean;
  matrixHasChanges?: boolean;
  onRequestUnlock?: () => void;
  onSaveDefaults?: () => void;
  onReset?: () => void;
  hideNextButton?: boolean;
  nextIsLocked?: boolean;
  hasStartedAnalysis?: boolean;
};

function getNextLabel(
  currentMainStep: number,
  isLastSector: boolean,
  matrixHasChanges?: boolean,
  hasStartedAnalysis?: boolean
): string {
  if (currentMainStep === 1) {
    return hasStartedAnalysis ? "Continuă analiza" : "Începe analiza";
  }
  if (currentMainStep === 2) {
    if (matrixHasChanges) return "Salvați modificările";
    return isLastSector ? "Finalizare sectoare" : "Următorul sector";
  }
  if (currentMainStep === 4) {
    return "Finalizare";
  }
  return "Următorul pas";
}

export function WizardNavigationFooter({
  currentMainStep,
  isLastSector,
  canGoBack,
  canGoNext,
  isSaving,
  onBack,
  onNext,
  onSkip,
  matrixIsLocked,
  matrixIsDefault,
  onRequestUnlock,
  onSaveDefaults,
  onReset,
  matrixHasChanges,
  hideNextButton,
  nextIsLocked,
  hasStartedAnalysis,
}: WizardNavigationFooterProps) {
  const nextLabel = getNextLabel(
    currentMainStep,
    isLastSector,
    matrixHasChanges,
    hasStartedAnalysis
  );
  const nextLabelClass =
    currentMainStep === 1 ? "inline" : "hidden lg:inline";
  const isMatrixStep = currentMainStep === 2;
  const showSaveDefaults =
    isMatrixStep && matrixIsLocked && matrixIsDefault && onSaveDefaults;

  return (
    <div className="flex flex-row items-center justify-between gap-1 md:gap-2">
      <div>
        {canGoBack && (
          <Button variant="ghost" onClick={onBack} title="Înapoi">
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden lg:inline">Înapoi</span>
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-1 md:gap-2">
        {onSkip && (
          <Button
            variant="ghost"
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            onClick={onSkip}
            title="Omite sectorul"
          >
            <SkipForward className="h-4 w-4" />
            <span className="hidden lg:inline">Omite sectorul</span>
          </Button>
        )}
        {isMatrixStep && matrixIsLocked && onRequestUnlock && (
          <Button
            variant="outline"
            onClick={onRequestUnlock}
            title="Deblocați pentru modificări"
          >
            <Unlock className="h-4 w-4" />
            <span className="hidden lg:inline">
              Deblocați pentru modificări
            </span>
          </Button>
        )}
        {onReset && (
          <Button variant="outline" onClick={onReset} title="Resetare valori">
            <RotateCcw className="h-4 w-4" />
            <span className="hidden lg:inline">Resetare valori</span>
          </Button>
        )}
      </div>

      <div>
        {hideNextButton ? null : showSaveDefaults ? (
          <Button
            variant="default"
            onClick={onSaveDefaults}
            disabled={isSaving}
            title="Salvați valorile implicite"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="hidden lg:inline">Se salvează...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span className="hidden lg:inline">
                  Salvați valorile implicite
                </span>
              </>
            )}
          </Button>
        ) : (
          <PaywallButton
            variant="default"
            onClick={onNext}
            disabled={!canGoNext || isSaving}
            title={nextLabel}
            locked={!!nextIsLocked && !isSaving}
            leftIcon={
              isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined
            }
            rightIcon={
              isSaving ? undefined : <ChevronRight className="h-4 w-4" />
            }
          >
            <span className={isSaving ? "hidden lg:inline" : nextLabelClass}>
              {isSaving ? "Se salvează..." : nextLabel}
            </span>
          </PaywallButton>
        )}
      </div>
    </div>
  );
}
